import type { AgencySettings, ConnectorsStatus, FormRegistryView, SettingsView } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, Database, FileUp, Image as ImageIcon, Sparkles, Upload, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { PoweredBy } from "../components/PoweredBy";
import { StateBadge } from "../components/StateBadge";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, Checkbox, Input, LoadingState, Select, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { fileToBase64 } from "../theme";

type Identity = AgencySettings["identity"];
type Profile = AgencySettings["profile"];
type Connections = AgencySettings["connections"];

const STEPS = ["identity", "profile", "connections", "data", "finish"] as const;
type StepId = (typeof STEPS)[number];

const TIMEZONES = ["America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/New_York", "America/Anchorage", "Pacific/Honolulu"];
const US_STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"];
const LINES = ["auto", "home", "renters", "commercial_auto", "general_liability", "bop", "life", "health", "other"] as const;
const SYSTEMS = ["Agency Matrix (Zywave)", "EZLynx", "HawkSoft", "Applied Epic", "AMS360", "QQ Catalyst", "NowCerts", "Jenesis", "Spreadsheets / paper", "Other", "None"];
const WORKFLOWS = ["certificates", "commercial_apps", "renewals", "texting", "carrier_downloads", "import", "faraday", "quoting"];
const STATIC_FORMS = [
  ["acord_23", "23 · Vehicle / equipment certificate"], ["acord_25", "25 · Liability certificate"], ["acord_27", "27 · Evidence of property (personal)"], ["acord_28", "28 · Evidence of commercial property"],
  ["acord_30", "30 · Garage certificate"], ["acord_35", "35 · Cancellation request"], ["acord_125", "125 · Commercial application"], ["acord_126", "126 · General liability section"],
  ["acord_130", "130 · Workers' compensation"], ["acord_131", "131 · Umbrella / excess"], ["acord_140", "140 · Property section"],
];

const lineLabel = (l: string) => l.replace(/_/g, " ");

export function SetupScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const q = useQuery({ queryKey: ["settings"], queryFn: async () => (await callAction<"agency.settings.get", SettingsView>("agency.settings.get", {})).result });
  const [step, setStep] = useState<number | null>(null);

  useEffect(() => {
    if (q.data && step === null) setStep(Math.min(4, q.data.settings.setup.step));
  }, [q.data, step]);

  const save = useMutation({
    mutationFn: (input: Parameters<typeof callAction<"agency.settings.update", SettingsView>>[1]) => callAction<"agency.settings.update", SettingsView>("agency.settings.update", input),
    onSuccess: (r) => {
      qc.setQueryData(["settings"], r.result);
      qc.invalidateQueries({ queryKey: ["config"] });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  if (q.isLoading || step === null) return <div className="min-h-screen flex items-center justify-center"><LoadingState /></div>;
  if (q.isError || !q.data) return <div className="min-h-screen flex items-center justify-center p-6 text-red-700">{(q.error as Error)?.message ?? t("common.error")}</div>;
  const view = q.data;
  const s = view.settings;
  const current = STEPS[step]!;

  const goto = async (next: number, patch: Record<string, unknown> = {}, exit?: "workspace" | "guide" | "import") => {
    const complete = next >= STEPS.length;
    await save.mutateAsync({ ...patch, setup: { step: complete ? STEPS.length - 1 : next, complete: complete || undefined } });
    toast({ title: t("setup.saved"), tone: "success", durationMs: 1200 });
    if (exit === "import") navigate("/import");
    else if (exit === "guide") navigate("/guide");
    else if (exit === "workspace" || complete) navigate("/");
    else setStep(next);
  };

  const skip = async () => {
    await save.mutateAsync({ setup: { skipped: true } });
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-white border-b border-ink/10">
        <div className="mx-auto max-w-4xl flex items-center gap-3 px-4 py-3">
          <img src={view.logoUrl ?? config.agency.brand.logoPath} alt="" className="h-9 w-auto" />
          <div className="flex-1 min-w-0">
            <p className="font-bold leading-tight heading">{t("setup.title")}</p>
            <p className="text-xs text-ink/55 truncate">{t("setup.subtitle")}</p>
          </div>
          <PoweredBy className="hidden sm:block" />
          <button onClick={skip} className="min-h-[40px] px-3 text-sm font-semibold text-ink/60 hover:text-ink">{t("setup.skip")}</button>
        </div>
        <div className="mx-auto max-w-4xl px-4 pb-3">
          <ol className="grid grid-cols-5 gap-1">
            {STEPS.map((id, i) => (
              <li key={id}>
                <button onClick={() => i < step && setStep(i)} disabled={i > step} className={cn("w-full text-left", i > step && "cursor-default")}>
                  <div className={cn("h-1.5 rounded-full", i < step ? "bg-primary" : i === step ? "bg-primary/60" : "bg-ink/10")} />
                  <span className={cn("mt-1 block text-[11px] font-semibold", i === step ? "text-ink" : "text-ink/50")}>{i + 1}. {t(`setup.step.${id}`)}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-5">
        <p className="text-xs font-semibold text-ink/50 mb-2">{t("setup.stepOf").replace("{n}", String(step + 1))}</p>
        {current === "identity" ? <IdentityStep view={view} saving={save.isPending} onBack={null} onNext={(patch) => goto(1, patch)} onExit={(patch) => goto(0, patch, "workspace")} /> : null}
        {current === "profile" ? <ProfileStep settings={s} saving={save.isPending} onBack={() => setStep(0)} onNext={(patch) => goto(2, patch)} onExit={(patch) => goto(1, patch, "workspace")} /> : null}
        {current === "connections" ? <ConnectionsStep settings={s} saving={save.isPending} onBack={() => setStep(1)} onNext={(patch) => goto(3, patch)} onExit={(patch) => goto(2, patch, "workspace")} /> : null}
        {current === "data" ? <DataStep view={view} onBack={() => setStep(2)} onNext={() => goto(4)} /> : null}
        {current === "finish" ? <FinishStep view={view} locale={locale} saving={save.isPending} onBack={() => setStep(3)} onOpen={() => goto(5, {}, s.startingData.choice === "import" ? "import" : "workspace")} onGuide={() => goto(5, {}, "guide")} /> : null}
      </main>
    </div>
  );
}

function Footer({ onBack, onExit, onNext, saving, nextLabel }: { onBack: (() => void) | null; onExit: () => void; onNext: () => void; saving: boolean; nextLabel?: string }) {
  const t = useT();
  return (
    <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
      {onBack ? <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4" />{t("setup.back")}</Button> : <span />}
      <div className="flex-1" />
      <Button variant="outline" onClick={onExit} disabled={saving}>{t("setup.saveExit")}</Button>
      <Button onClick={onNext} loading={saving}>{nextLabel ?? t("setup.next")}<ArrowRight className="h-4 w-4" /></Button>
    </div>
  );
}

function StepCard({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <Card className="p-5 md:p-6">
      <h1 className="text-2xl font-bold">{title}</h1>
      <p className="text-ink/60 mb-4">{sub}</p>
      {children}
    </Card>
  );
}

// ---------------- Step 1: identity ----------------

function IdentityStep({ view, saving, onBack, onNext, onExit }: { view: SettingsView; saving: boolean; onBack: null; onNext: (patch: { identity: Partial<Identity> }) => void; onExit: (patch: { identity: Partial<Identity> }) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const { toast } = useToast();
  const id = view.settings.identity;
  // Blank settings start from the compiled agency overlay (the prospect's own brand when one exists), so a branded demo
  // opens with their name, phone and offices already filled and the wizard only asks them to confirm or correct.
  const { config } = useConfig();
  const a = config.agency;
  const [f, setF] = useState({
    legalName: id.legalName ?? a.legalName ?? "", dba: id.dba ?? a.dba ?? "", displayName: id.displayName ?? a.displayName ?? "", phone: id.phone ?? a.contact.phone ?? "", email: id.email ?? a.contact.email ?? "", website: id.website ?? a.contact.website ?? "",
    timezone: id.timezone ?? a.timezone ?? "America/Chicago", defaultLocale: id.defaultLocale ?? a.defaultLocale ?? "en", ein: id.ein ?? "", npn: id.npn ?? "", fictional: id.fictional,
    line1: id.address?.line1 ?? "", line2: id.address?.line2 ?? "", city: id.address?.city ?? "", state: id.address?.state ?? a.state ?? "TX", zip: id.address?.zip ?? "",
    licenses: id.licenses.length ? id.licenses.map((l) => ({ ...l, kind: l.kind ?? "" })) : [],
  });
  const set = (k: keyof typeof f, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const logo = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 500 * 1024) throw new Error(t("setup.identity.logoHelp"));
      const base64 = await fileToBase64(file);
      return callAction<"agency.logo.upload", SettingsView>("agency.logo.upload", { contentType: file.type || "image/png", base64 });
    },
    onSuccess: (r) => {
      qc.setQueryData(["settings"], r.result);
      qc.invalidateQueries({ queryKey: ["config"] });
      toast({ title: t("setup.identity.logoUploaded"), tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  const patch = (): { identity: Partial<Identity> } => {
    const addr = f.line1.trim() && f.city.trim() ? { line1: f.line1.trim(), line2: f.line2.trim() || undefined, city: f.city.trim(), state: f.state.toUpperCase(), zip: f.zip.trim() } : undefined;
    const clean = (v: string) => (v.trim() ? v.trim() : undefined);
    return {
      identity: {
        legalName: clean(f.legalName), dba: clean(f.dba), displayName: clean(f.displayName), phone: clean(f.phone), email: clean(f.email), website: clean(f.website),
        timezone: f.timezone, defaultLocale: f.defaultLocale as "en" | "es", ein: clean(f.ein), npn: clean(f.npn), fictional: f.fictional, address: addr,
        licenses: f.licenses.filter((l) => l.number.trim()).map((l) => ({ state: l.state.toUpperCase(), number: l.number.trim(), kind: l.kind.trim() || undefined })),
      },
    };
  };

  return (
    <StepCard title={t("setup.identity.title")} sub={t("setup.identity.sub")}>
      <div className="grid gap-4 md:grid-cols-2">
        <Input label={t("setup.identity.legalName")} value={f.legalName} onChange={(e) => set("legalName", e.target.value)} placeholder="Horizon Shield Insurance Agency LLC" />
        <Input label={t("setup.identity.dba")} value={f.dba} onChange={(e) => set("dba", e.target.value)} />
        <Input label={t("setup.identity.displayName")} value={f.displayName} onChange={(e) => set("displayName", e.target.value)} placeholder={f.dba || f.legalName || "Agency Desk"} />
        <div>
          <span className="mb-1 block text-sm font-semibold text-ink/80">{t("setup.identity.logo")}</span>
          <div className="flex items-center gap-3">
            <div className="h-14 w-24 rounded-xl border border-ink/10 bg-white flex items-center justify-center overflow-hidden">
              {view.logoUrl ? <img src={view.logoUrl} alt="" className="max-h-12 max-w-[88px]" /> : <ImageIcon className="h-6 w-6 text-ink/30" />}
            </div>
            <label className="inline-flex min-h-tap cursor-pointer items-center gap-2 rounded-xl border border-ink/15 bg-white px-4 text-sm font-semibold hover:bg-ink/5">
              <Upload className="h-4 w-4" />{logo.isPending ? t("common.loading") : t("setup.identity.logoPick")}
              <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="sr-only" onChange={(e) => { const file = e.target.files?.[0]; if (file) logo.mutate(file); e.target.value = ""; }} />
            </label>
          </div>
          <p className="mt-1 text-xs text-ink/50">{t("setup.identity.logoHelp")}</p>
        </div>
      </div>

      <h2 className="mt-5 mb-2 text-base font-bold">{t("setup.identity.address")}</h2>
      <div className="grid gap-3 md:grid-cols-6">
        <div className="md:col-span-4"><Input label={t("setup.identity.line1")} value={f.line1} onChange={(e) => set("line1", e.target.value)} /></div>
        <div className="md:col-span-2"><Input label={t("setup.identity.line2")} value={f.line2} onChange={(e) => set("line2", e.target.value)} /></div>
        <div className="md:col-span-3"><Input label={t("setup.identity.city")} value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
        <div className="md:col-span-1">
          <Select label={t("setup.identity.state")} value={f.state} onChange={(e) => set("state", e.target.value)}>{US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}</Select>
        </div>
        <div className="md:col-span-2"><Input label={t("setup.identity.zip")} value={f.zip} onChange={(e) => set("zip", e.target.value)} inputMode="numeric" /></div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <Input label={t("setup.identity.phone")} type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(915) 555-0100" />
        <Input label={t("setup.identity.email")} type="email" value={f.email} onChange={(e) => set("email", e.target.value)} />
        <Input label={t("setup.identity.website")} value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" />
        <Select label={t("setup.identity.timezone")} value={f.timezone} onChange={(e) => set("timezone", e.target.value)}>{TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}</Select>
        <Select label={t("setup.identity.locale")} value={f.defaultLocale} onChange={(e) => set("defaultLocale", e.target.value)}>
          <option value="en">{t("common.english")}</option>
          <option value="es">{t("common.spanish")}</option>
        </Select>
        <div className="flex items-end"><Checkbox label={t("setup.identity.fictional")} checked={f.fictional} onChange={(v) => set("fictional", v)} /></div>
      </div>

      <h2 className="mt-5 mb-1 text-base font-bold">{t("setup.identity.ids")}</h2>
      <p className="text-xs text-ink/55 mb-2">{t("setup.identity.idsHelp")}</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Input label={t("setup.identity.ein")} value={f.ein} onChange={(e) => set("ein", e.target.value)} placeholder="12-3456789" />
        <Input label={t("setup.identity.npn")} value={f.npn} onChange={(e) => set("npn", e.target.value)} />
      </div>
      <div className="mt-3">
        <span className="mb-1 block text-sm font-semibold text-ink/80">{t("setup.identity.licenses")}</span>
        <ul className="space-y-2">
          {f.licenses.map((l, i) => (
            <li key={i} className="grid gap-2 sm:grid-cols-[90px_1fr_1fr_auto] sm:items-end">
              <Select aria-label={t("setup.identity.state")} value={l.state} onChange={(e) => set("licenses", f.licenses.map((x, j) => (j === i ? { ...x, state: e.target.value } : x)))}>{US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}</Select>
              <Input aria-label={t("setup.identity.licenseNumber")} placeholder={t("setup.identity.licenseNumber")} value={l.number} onChange={(e) => set("licenses", f.licenses.map((x, j) => (j === i ? { ...x, number: e.target.value } : x)))} />
              <Input aria-label={t("setup.identity.licenseKind")} placeholder={t("setup.identity.licenseKind")} value={l.kind} onChange={(e) => set("licenses", f.licenses.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))} />
              <Button variant="ghost" size="sm" onClick={() => set("licenses", f.licenses.filter((_, j) => j !== i))} aria-label={t("setup.identity.remove")}><X className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
        <Button variant="outline" size="sm" className="mt-2" onClick={() => set("licenses", [...f.licenses, { state: f.state || "TX", number: "", kind: "" }])}>{t("setup.identity.addLicense")}</Button>
        <p className="mt-2 text-xs text-ink/50">{t("setup.identity.producerNote")}</p>
      </div>
      <Footer onBack={onBack} onExit={() => onExit(patch())} onNext={() => onNext(patch())} saving={saving} />
    </StepCard>
  );
}

// ---------------- Step 2: profile ----------------

function Chip({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} className={cn("min-h-[36px] rounded-full border px-3 text-sm font-semibold", on ? "border-primary bg-primary/12 text-primary-dark" : "border-ink/15 bg-white text-ink/70 hover:bg-ink/5")}>
      {on ? <Check className="inline h-3.5 w-3.5 mr-1" /> : null}{label}
    </button>
  );
}

function ProfileStep({ settings, saving, onBack, onNext, onExit }: { settings: AgencySettings; saving: boolean; onBack: () => void; onNext: (patch: { profile: Partial<Profile> }) => void; onExit: (patch: { profile: Partial<Profile> }) => void }) {
  const t = useT();
  const { locale } = useLocale();
  const p = settings.profile;
  const [statesServed, setStates] = useState<string[]>(p.statesServed.length ? p.statesServed : [settings.identity.address?.state ?? "TX"]);
  const [stateToAdd, setStateToAdd] = useState("");
  const [lines, setLines] = useState<string[]>(p.lines);
  const [products, setProducts] = useState<string[]>(p.products);
  const [productInput, setProductInput] = useState("");
  const [carriers, setCarriers] = useState(p.carriers.map((c) => ({ ...c })));
  const [carrierInput, setCarrierInput] = useState("");
  const [currentSystem, setSystem] = useState(p.currentSystem ?? "");
  const [formsUsed, setForms] = useState<string[]>(p.formsUsed);
  const [workflows, setWorkflows] = useState<string[]>(p.workflows);
  const registry = useQuery({ queryKey: ["form-registry"], queryFn: async () => (await callAction<"agency.form.registry", FormRegistryView>("agency.form.registry", {})).result, retry: false, staleTime: 300_000 });
  const formOptions = useMemo(() => registry.data?.forms?.length ? registry.data.forms.map((fd) => [fd.id, `${fd.number} · ${locale === "es" ? fd.title.es : fd.title.en}`] as const) : STATIC_FORMS, [registry.data, locale]);

  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const patch = (): { profile: Partial<Profile> } => ({ profile: { statesServed, lines: lines as Profile["lines"], products, carriers: carriers.filter((c) => c.name.trim()) as Profile["carriers"], currentSystem: currentSystem || undefined, formsUsed, workflows } });

  return (
    <StepCard title={t("setup.profile.title")} sub={t("setup.profile.sub")}>
      <h2 className="text-base font-bold mb-2">{t("setup.profile.states")}</h2>
      <div className="flex flex-wrap items-center gap-2">
        {statesServed.map((st) => <Chip key={st} on label={st} onClick={() => setStates(statesServed.filter((x) => x !== st))} />)}
        <Select aria-label={t("setup.profile.addState")} className="w-28" value={stateToAdd} onChange={(e) => { if (e.target.value && !statesServed.includes(e.target.value)) setStates([...statesServed, e.target.value]); setStateToAdd(""); }}>
          <option value="">{t("setup.profile.addState")}</option>
          {US_STATES.filter((st) => !statesServed.includes(st)).map((st) => <option key={st} value={st}>{st}</option>)}
        </Select>
      </div>

      <h2 className="mt-5 text-base font-bold mb-2">{t("setup.profile.lines")}</h2>
      <div className="flex flex-wrap gap-2">{LINES.map((l) => <Chip key={l} on={lines.includes(l)} label={lineLabel(l)} onClick={() => toggle(lines, setLines, l)} />)}</div>

      <h2 className="mt-5 text-base font-bold mb-2">{t("setup.profile.products")}</h2>
      <div className="flex flex-wrap items-center gap-2">
        {products.map((pr) => <Chip key={pr} on label={pr} onClick={() => setProducts(products.filter((x) => x !== pr))} />)}
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); const v = productInput.trim(); if (v && !products.includes(v)) setProducts([...products, v]); setProductInput(""); }}>
          <Input aria-label={t("setup.profile.products")} className="min-h-[36px] w-64" placeholder={t("setup.profile.productPlaceholder")} value={productInput} onChange={(e) => setProductInput(e.target.value)} />
          <Button type="submit" variant="outline" size="sm">{t("setup.profile.add")}</Button>
        </form>
      </div>

      <h2 className="mt-5 text-base font-bold mb-2">{t("setup.profile.carriers")}</h2>
      <ul className="space-y-2">
        {carriers.map((c, i) => (
          <li key={i} className="rounded-2xl border border-ink/10 bg-surface p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input aria-label={t("setup.profile.carrierName")} className="min-h-[40px] flex-1 min-w-[180px]" value={c.name} onChange={(e) => setCarriers(carriers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <Select aria-label={t("setup.profile.carrierKind")} className="min-h-[40px] w-36" value={c.kind} onChange={(e) => setCarriers(carriers.map((x, j) => (j === i ? { ...x, kind: e.target.value as "carrier" | "mga" } : x)))}>
                <option value="carrier">{t("setup.profile.kind.carrier")}</option>
                <option value="mga">{t("setup.profile.kind.mga")}</option>
              </Select>
              <Button variant="ghost" size="sm" onClick={() => setCarriers(carriers.filter((_, j) => j !== i))} aria-label={t("setup.identity.remove")}><X className="h-4 w-4" /></Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {LINES.map((l) => <Chip key={l} on={c.lines.includes(l)} label={lineLabel(l)} onClick={() => setCarriers(carriers.map((x, j) => (j === i ? { ...x, lines: (x.lines.includes(l) ? x.lines.filter((y) => y !== l) : [...x.lines, l]) as typeof x.lines } : x)))} />)}
            </div>
          </li>
        ))}
      </ul>
      <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); const v = carrierInput.trim(); if (v) setCarriers([...carriers, { name: v, kind: "carrier", lines: [] }]); setCarrierInput(""); }}>
        <Input aria-label={t("setup.profile.carrierName")} className="min-h-[40px] w-72" placeholder={t("setup.profile.carrierName")} value={carrierInput} onChange={(e) => setCarrierInput(e.target.value)} />
        <Button type="submit" variant="outline" size="sm">{t("setup.profile.addCarrier")}</Button>
      </form>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <Select label={t("setup.profile.currentSystem")} value={currentSystem} onChange={(e) => setSystem(e.target.value)}>
          <option value="">—</option>
          {SYSTEMS.map((sys) => <option key={sys} value={sys}>{sys}</option>)}
        </Select>
      </div>

      <h2 className="mt-5 text-base font-bold mb-2">{t("setup.profile.forms")}</h2>
      <div className="flex flex-wrap gap-2">{formOptions.map(([id, label]) => <Chip key={id} on={formsUsed.includes(id)} label={label} onClick={() => toggle(formsUsed, setForms, id)} />)}</div>

      <h2 className="mt-5 text-base font-bold mb-2">{t("setup.profile.workflows")}</h2>
      <div className="grid gap-2 sm:grid-cols-2">{WORKFLOWS.map((w) => <Checkbox key={w} label={t(`setup.profile.workflow.${w}`)} checked={workflows.includes(w)} onChange={() => toggle(workflows, setWorkflows, w)} />)}</div>

      <Footer onBack={onBack} onExit={() => onExit(patch())} onNext={() => onNext(patch())} saving={saving} />
    </StepCard>
  );
}

// ---------------- Step 3: connections ----------------

function ConnectionsStep({ settings, saving, onBack, onNext, onExit }: { settings: AgencySettings; saving: boolean; onBack: () => void; onNext: (patch: { connections: Partial<Connections> }) => void; onExit: (patch: { connections: Partial<Connections> }) => void }) {
  const t = useT();
  const c = settings.connections;
  const [formsProvider, setFormsProvider] = useState(c.formsProvider);
  const [wanted, setWanted] = useState(c.texting.wanted);
  const [sender, setSender] = useState(c.texting.sender);
  const [allowlistOnly, setAllowlist] = useState(c.texting.allowlistOnly);
  const [carrierMethod, setMethod] = useState(c.carrierMethod);
  const status = useQuery({ queryKey: ["connectors"], queryFn: async () => (await callAction<"agency.connectors.status", ConnectorsStatus>("agency.connectors.status", {})).result, staleTime: 30_000 });
  const patch = (): { connections: Partial<Connections> } => ({ connections: { formsProvider, texting: { wanted, sender, allowlistOnly }, carrierMethod } });

  return (
    <StepCard title={t("setup.connections.title")} sub={t("setup.connections.sub")}>
      <h2 className="text-base font-bold mb-2">{t("setup.connections.forms")}</h2>
      <div className="grid gap-2 md:grid-cols-3">
        {(["formcruise", "simply_easier", "undecided"] as const).map((opt) => (
          <button key={opt} type="button" onClick={() => setFormsProvider(opt)} aria-pressed={formsProvider === opt} className={cn("rounded-2xl border p-3 text-left min-h-[88px]", formsProvider === opt ? "border-primary bg-primary/8" : "border-ink/10 bg-white hover:bg-ink/5")}>
            <p className="font-semibold">{t(`setup.connections.forms.${opt}`)}</p>
            <p className="text-xs text-ink/60 mt-1">{t(`setup.connections.forms.${opt}Note`)}</p>
            {formsProvider === opt && opt !== "undecided" ? <Badge tone="amber" className="mt-2">{t("setup.connections.configured")}</Badge> : null}
          </button>
        ))}
      </div>

      <h2 className="mt-5 text-base font-bold mb-2">{t("setup.connections.texting")}</h2>
      <Checkbox label={t("setup.connections.texting.wanted")} checked={wanted} onChange={setWanted} />
      {wanted ? (
        <div className="mt-2 space-y-2 rounded-2xl bg-surface p-3">
          <p className="text-sm font-semibold text-ink/80">{t("setup.connections.texting.sender")}</p>
          {(["agency_number", "demo_number", "undecided"] as const).map((opt) => (
            <label key={opt} className="flex min-h-[36px] cursor-pointer items-center gap-2 text-sm">
              <input type="radio" name="sender" className="h-4 w-4 accent-[var(--brand-primary)]" checked={sender === opt} onChange={() => setSender(opt)} />
              {t(`setup.connections.texting.sender.${opt}`)}
            </label>
          ))}
          <Checkbox label={t("setup.connections.texting.allowlist")} checked={allowlistOnly} onChange={setAllowlist} />
        </div>
      ) : null}

      <h2 className="mt-5 text-base font-bold mb-2">{t("setup.connections.carrier")}</h2>
      <Select aria-label={t("setup.connections.carrier")} value={carrierMethod} onChange={(e) => setMethod(e.target.value as typeof carrierMethod)}>
        {(["undecided", "ivans", "api", "exported_files", "portal_automation", "document_ingestion"] as const).map((m) => <option key={m} value={m}>{t(`setup.connections.carrier.${m}`)}</option>)}
      </Select>

      <h2 className="mt-5 text-base font-bold">{t("setup.connections.states")}</h2>
      <p className="text-xs text-ink/55 mb-2">{t("setup.connections.statesSub")}</p>
      <ul className="divide-y divide-ink/5 rounded-2xl border border-ink/10 bg-white">
        {(status.data?.connectors ?? []).map((row) => (
          <li key={row.id} className="flex items-center gap-3 px-4 py-2 text-sm">
            <span className="flex-1 min-w-0"><span className="font-semibold">{row.label}</span> <span className="text-ink/50">· {row.provider}</span></span>
            {row.id === "forms" && formsProvider !== "undecided" ? <Badge tone="amber">{t("settings.configured")}: {formsProvider}</Badge> : null}
            <StateBadge state={row.state} />
          </li>
        ))}
        {status.isLoading ? <li className="px-4 py-2 text-sm text-ink/55">{t("common.loading")}</li> : null}
      </ul>
      <Footer onBack={onBack} onExit={() => onExit(patch())} onNext={() => onNext(patch())} saving={saving} />
    </StepCard>
  );
}

// ---------------- Step 4: starting data ----------------

function DataStep({ view, onBack, onNext }: { view: SettingsView; onBack: () => void; onNext: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const { toast } = useToast();
  const choice = view.settings.startingData.choice;
  const starting = useMutation({
    mutationFn: (c: "sample" | "empty") => callAction<"agency.settings.starting_data", SettingsView>("agency.settings.starting_data", { choice: c }),
    onSuccess: (r) => { qc.setQueryData(["settings"], r.result); qc.invalidateQueries(); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const markImport = useMutation({
    mutationFn: () => callAction<"agency.settings.update", SettingsView>("agency.settings.update", { startingData: { choice: "import" } }),
    onSuccess: (r) => qc.setQueryData(["settings"], r.result),
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const busy = starting.isPending || markImport.isPending;
  const cards: Array<{ id: "sample" | "import" | "empty"; icon: React.ReactNode; chosenLabel: string; act: () => void }> = [
    { id: "sample", icon: <Database className="h-6 w-6" />, chosenLabel: t("setup.data.sampleLoaded"), act: () => starting.mutate("sample") },
    { id: "import", icon: <FileUp className="h-6 w-6" />, chosenLabel: t("setup.data.importChosen"), act: () => markImport.mutate() },
    { id: "empty", icon: <Sparkles className="h-6 w-6" />, chosenLabel: t("setup.data.emptyChosen"), act: () => starting.mutate("empty") },
  ];
  return (
    <StepCard title={t("setup.data.title")} sub={t("setup.data.sub")}>
      <div className="grid gap-3 md:grid-cols-3">
        {cards.map((c) => {
          const on = choice === c.id;
          return (
            <div key={c.id} className={cn("rounded-2xl border p-4 flex flex-col gap-2", on ? "border-primary bg-primary/8" : "border-ink/10 bg-white")}>
              <span className="text-primary-dark">{c.icon}</span>
              <p className="font-bold">{t(`setup.data.${c.id}`)}</p>
              <p className="text-sm text-ink/60 flex-1">{t(`setup.data.${c.id}Sub`)}</p>
              {on ? <Badge tone="green"><Check className="h-3 w-3" /> {c.chosenLabel}</Badge> : <Button variant="outline" size="sm" onClick={c.act} loading={busy}>{t("setup.data.choose")}</Button>}
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-ink/50">{t("settings.demoData.counts").replace("{customers}", String(view.counts.customers)).replace("{sample}", String(view.counts.sampleCustomers)).replace("{policies}", String(view.counts.policies))}</p>
      <Footer onBack={onBack} onExit={onNext} onNext={onNext} saving={busy} />
    </StepCard>
  );
}

// ---------------- Step 5: finish ----------------

function FinishStep({ view, locale, saving, onBack, onOpen, onGuide }: { view: SettingsView; locale: "en" | "es"; saving: boolean; onBack: () => void; onOpen: () => void; onGuide: () => void }) {
  const t = useT();
  const s = view.settings;
  const none = t("setup.finish.none");
  const rows: Array<[string, string]> = [
    [t("setup.finish.agency"), [s.identity.displayName || s.identity.dba || s.identity.legalName, s.identity.fictional ? `(${t("settings.agency.fictional")})` : ""].filter(Boolean).join(" ") || none],
    [t("setup.finish.lines"), s.profile.lines.map(lineLabel).join(", ") || none],
    [t("setup.finish.carriers"), s.profile.carriers.map((c) => c.name).join(", ") || none],
    [t("setup.finish.forms"), s.connections.formsProvider === "undecided" ? none : `${t(`setup.connections.forms.${s.connections.formsProvider}`)} · ${t("setup.connections.configured")}`],
    [t("setup.finish.texting"), s.connections.texting.wanted ? `${t("setup.finish.texting.on")} · ${t(`setup.connections.texting.sender.${s.connections.texting.sender}`)}` : t("setup.finish.texting.off")],
    [t("setup.finish.data"), s.startingData.choice ? t(`setup.data.${s.startingData.choice}`) : none],
  ];
  return (
    <StepCard title={t("setup.finish.title")} sub={t("setup.finish.sub")}>
      <dl className="divide-y divide-ink/5 rounded-2xl border border-ink/10 bg-white">
        {rows.map(([k, v]) => (
          <div key={k} className="grid gap-1 px-4 py-2 text-sm sm:grid-cols-[180px_1fr]">
            <dt className="text-ink/55">{k}</dt>
            <dd className="font-semibold">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-xs text-ink/50">{locale === "es" ? "Todo se puede editar en Ajustes." : "Everything can be edited in Settings."} <Link to="/settings" className="underline">{t("nav.settings")}</Link></p>
      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
        <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4" />{t("setup.back")}</Button>
        <div className="flex-1" />
        <Button variant="outline" size="lg" onClick={onGuide} loading={saving}>{t("setup.finish.guide")}</Button>
        <Button size="lg" onClick={onOpen} loading={saving}>{t("setup.finish.open")}<ArrowRight className="h-4 w-4" /></Button>
      </div>
    </StepCard>
  );
}
